import React, { useState } from 'react';
import { Star, X, Send, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const StarRating = ({ rating, setRating, size = 'lg', label, readonly = false }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizes = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-10 h-10'
  };
  
  return (
    <div className="flex flex-col items-center gap-1">
      {label && <span className="text-gray-400 text-xs">{label}</span>}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && setRating(star)}
            onMouseEnter={() => !readonly && setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className={`transition-transform ${!readonly && 'hover:scale-110 cursor-pointer'}`}
          >
            <Star
              className={`${sizes[size]} ${
                star <= (hoverRating || rating)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-600'
              } transition-colors`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const OrderRatingDialog = ({ order, isOpen, onClose, onRatingSubmitted }) => {
  const [overallRating, setOverallRating] = useState(0);
  const [foodRating, setFoodRating] = useState(0);
  const [deliveryRating, setDeliveryRating] = useState(0);
  const [packagingRating, setPackagingRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (overallRating === 0) {
      toast.error('Please select an overall rating');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post(`/orders/${order.id}/rating`, {
        rating: overallRating,
        food_rating: foodRating || null,
        delivery_rating: order.order_type === 'delivery' ? deliveryRating || null : null,
        packaging_rating: packagingRating || null,
        comment: comment.trim() || null
      });

      toast.success(response.data.message || 'Thank you for your feedback!');
      onRatingSubmitted && onRatingSubmitted();
      onClose();
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setIsSubmitting(false);
    }
  };

  const quickComments = [
    'Delicious food!',
    'Great packaging',
    'Fast delivery',
    'Will order again',
    'Loved it!'
  ];

  if (!order) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-center">
            Rate Your Order
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Summary */}
          <div className="bg-white/5 rounded-lg p-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-[#00E5FF] font-mono text-sm">{order.order_number}</span>
              <span className="text-white font-medium">{formatCurrency(order.total_amount)}</span>
            </div>
            <div className="text-gray-400 text-xs">
              {order.items?.slice(0, 2).map((item, idx) => (
                <span key={idx}>{item.quantity}x {item.product_name}{idx < 1 && order.items.length > 1 ? ', ' : ''}</span>
              ))}
              {order.items?.length > 2 && <span> +{order.items.length - 2} more</span>}
            </div>
          </div>

          {/* Overall Rating */}
          <div className="text-center">
            <p className="text-white mb-3">How was your overall experience?</p>
            <StarRating rating={overallRating} setRating={setOverallRating} size="xl" />
            <p className="text-gray-500 text-sm mt-2">
              {overallRating === 1 && 'Poor'}
              {overallRating === 2 && 'Fair'}
              {overallRating === 3 && 'Good'}
              {overallRating === 4 && 'Very Good'}
              {overallRating === 5 && 'Excellent!'}
            </p>
          </div>

          {/* Category Ratings */}
          <AnimatePresence>
            {overallRating > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="grid grid-cols-3 gap-4"
              >
                <StarRating 
                  rating={foodRating} 
                  setRating={setFoodRating} 
                  size="sm" 
                  label="Food"
                />
                {order.order_type === 'delivery' && (
                  <StarRating 
                    rating={deliveryRating} 
                    setRating={setDeliveryRating} 
                    size="sm" 
                    label="Delivery"
                  />
                )}
                <StarRating 
                  rating={packagingRating} 
                  setRating={setPackagingRating} 
                  size="sm" 
                  label="Packaging"
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Quick Comments */}
          <div>
            <p className="text-gray-400 text-sm mb-2">Quick feedback (tap to add)</p>
            <div className="flex flex-wrap gap-2">
              {quickComments.map((qc, idx) => (
                <button
                  key={idx}
                  onClick={() => setComment(prev => 
                    prev.includes(qc) ? prev.replace(qc, '').trim() : `${prev} ${qc}`.trim()
                  )}
                  className={`px-3 py-1 rounded-full text-xs transition-colors ${
                    comment.includes(qc) 
                      ? 'bg-[#FF4500] text-white' 
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {qc}
                </button>
              ))}
            </div>
          </div>

          {/* Comment Input */}
          <div>
            <p className="text-gray-400 text-sm mb-2">Additional comments (optional)</p>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell us more about your experience..."
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none"
              rows={3}
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={overallRating === 0 || isSubmitting}
            className="w-full bg-[#FF4500] hover:bg-[#FF4500]/90 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Rating
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Compact rating display for order cards
export const OrderRatingBadge = ({ rating, size = 'sm' }) => {
  if (!rating) return null;
  
  return (
    <div className="flex items-center gap-1">
      <Star className={`${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} fill-yellow-400 text-yellow-400`} />
      <span className={`text-yellow-400 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>{rating}</span>
    </div>
  );
};

// Rate Order Button
export const RateOrderButton = ({ order, onRatingSubmitted }) => {
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  
  // Don't show if already rated or not delivered/completed
  if (order.rating || !['delivered', 'completed'].includes(order.status)) {
    return null;
  }
  
  return (
    <>
      <Button
        onClick={() => setShowRatingDialog(true)}
        variant="outline"
        size="sm"
        className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
      >
        <Star className="w-4 h-4 mr-1" />
        Rate Order
      </Button>
      
      <OrderRatingDialog
        order={order}
        isOpen={showRatingDialog}
        onClose={() => setShowRatingDialog(false)}
        onRatingSubmitted={onRatingSubmitted}
      />
    </>
  );
};

export default OrderRatingDialog;
